import { Inject, Injectable, Injector, inject } from '@angular/core';
import dayjs from 'dayjs';
import sortBy from 'lodash-es/sortBy';
import pick from 'lodash-es/pick';
import extend from 'lodash-es/extend';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { IFBLevel } from '@interfaces';

import { CompanyService } from '@app/shared/services/company.service';
import { IFBWindow } from '@app/models/window-namespace';
import { JobResource } from '@app/shared/modules/resources/job.resource';
import { LevelResource } from '@app/shared/modules/resources/level.resource';
import { logger } from '@utils/logger.service';
import { SsoService } from '@app/shared/services/sso.service';
import { UserManager } from '@app/shared/services/user-manager.service';
import { UserStatisticsService } from '@app/shared/services/user-statistics.service';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class AppcuesService {
  // Will be defined at the runtime with $injector to prevent circular dependency
  private UserManager!: UserManager;
  private UserStatistics!: UserStatisticsService;
  private SSO: SsoService;
  private readonly http = inject(HttpClient);
  private streamUserToken: string;

  constructor(
    @Inject('Window') private window: IFBWindow,
    private companyService: CompanyService,
    private injector: Injector,
    private jobResource: JobResource,
    private levelResource: LevelResource
  ) {}

  anonymous() {
    if (this.window.Appcues) {
      this.window.Appcues.anonymous();
    }
  }

  track(event: string, value?: any) {
    if (this.window.Appcues) {
      this.window.Appcues.track(event, value);
    }
  }

  page() {
    if (this.window.Appcues) {
      this.window.Appcues.page();
    }
  }

  setShowReferModal(value: 'off' | 'on') {
    this.identify(value, true);
  }

  /**
   * @param showReferModal {String} on|off
   * @param fireStart {Boolean}
   */
  identify(showReferModal: 'off' | 'on' = 'off', fireStart: boolean = false) {
    logger.info('Appcues identify');

    if (!this.window.Appcues) {
      logger.warn('No appcues lib found');
      return;
    }

    // Inject services at runtime so we don't need pass them through function arguments
    this.UserManager = this.injector.get(UserManager);
    this.UserStatistics = this.injector.get(UserStatisticsService);
    this.SSO = this.injector.get(SsoService);

    forkJoin({
      activeJobs: this.trackActiveJobs(),
      activityLastMonth: this.getUserToken().pipe(switchMap(token => this.trackActivityLastMonth(token))),
      userAgeOverMonth: this.trackUserAgeOverMonth(),
      userCurrentLevel: this.trackUserCurrentLevel(),
      ssoActive: this.trackSsoActive()
    }).subscribe(
      customValues => {
        const data = {
          company: this.companyService.subdomain,
          employeeIdSet: Boolean(this.UserManager.data.employeeId),
          locale: this.UserManager.data.locale,
          role: this.UserManager.roleInCompany(pick(this.UserManager.data, 'roles')),
          showReferModal
        };

        this.window.Appcues.identify(this.UserManager.data.id, extend(data, customValues));

        if (fireStart === true) {
          // Make it trigger Appcues flows if new properties require it
          this.window.Appcues.start();
        }
      },
      err => logger.error(err)
    );
  }

  // Set a property "Activity last month".
  // It should be true if:
  //   there is any achievement in the last month (inclusive) with points > 0
  // It should be false if:
  //   there is no achievement for the last month
  trackActivityLastMonth(userToken: string) {
    if (!(window as any).stream) {
      logger.error('Stream SDK not loaded. Make sure the CDN script is included.');
      return;
    }

    const deferred$ = new Subject();

    if (!this.UserManager.isLoggedIn || !this.window.Appcues) {
      deferred$.next(undefined);
      deferred$.complete();

      return deferred$;
    }

    const now = dayjs();
    const client = (window as any).stream.connect(
      environment.GETSTREAM.API_KEY,
      userToken,
      environment.GETSTREAM.APP_ID
    );
    const feed = client.feed('achievements', this.UserManager.data.id as string);
    feed
      .get({ limit: 1 })
      .then((response: any) => {
        if (response.results.length) {
          const lastActivityCreatedTime = dayjs(response.results[0].created_at);
          const monthsPassedSinceActivity = now.diff(lastActivityCreatedTime, 'months');

          deferred$.next(monthsPassedSinceActivity === 0);
          deferred$.complete();
        } else {
          deferred$.next(undefined);
          deferred$.complete();
        }
      })
      .catch(err => console.error('debug appcues get', err));

    return deferred$;
  }

  getUserToken() {
    if (this.streamUserToken) {
      return of(this.streamUserToken);
    }

    return this.http
      .get<{ config: { stream: { token: string } } }>(
        `https://webapp.${environment.SERVICES_BASE}/common/api/config?keys=stream`,
        {
          headers: {
            Accept: 'application/json',
            'X-1brd-Host': this.companyService.x1brdHost
          }
        }
      )
      .pipe(
        map(response => response.config.stream.token),
        tap(token => (this.streamUserToken = token))
      );
  }

  // Set a property "Active Jobs", which is the number of jobs in ACTIVE_PUBLISHED state.
  trackActiveJobs() {
    if (!this.UserManager.isLoggedIn || !this.window.Appcues) {
      return of(undefined);
    }

    return this.jobResource.getPage({ limit: 1, status: 'ACTIVE_PUBLISHED' }).pipe(
      map(response => response.total),
      catchError(err => {
        logger.warn(err);
        return of(0);
      })
    );
  }

  // Set a property "User age over month".
  trackUserAgeOverMonth() {
    if (!this.UserManager.isLoggedIn || !this.window.Appcues) {
      return of(undefined);
    }

    const now = dayjs();
    const monthsPassedSinceRegister = now.diff(dayjs(this.UserManager.data.createdAt), 'months');
    return of(monthsPassedSinceRegister > 0);
  }

  // Set a property "User Current Level".
  trackUserCurrentLevel() {
    if (!this.UserManager.isLoggedIn || !this.window.Appcues) {
      return of(undefined);
    }

    return this.UserStatistics.fetch().pipe(
      switchMap(() => {
        return this.levelResource.getAll();
      }),
      map(levels => {
        const currentLevel = sortBy(levels)
          .reverse()
          .find((level: IFBLevel) => level.points <= this.UserStatistics.data.points);

        if (currentLevel) {
          return currentLevel.level;
        }
        return 1;
      }),
      catchError(err => {
        logger.warn(err);
        return of(1);
      })
    );
  }

  trackSsoActive() {
    if (!this.UserManager.isLoggedIn || !this.window.Appcues) {
      return of(undefined);
    }

    return this.SSO.fetchIdentityProviders().pipe(
      map(() => this.SSO.isEnabled),
      catchError(err => {
        logger.warn(err);
        return of(this.SSO.isEnabled);
      })
    );
  }
}
